<script setup>
import { computed } from 'vue';
import { useStoreGetters } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { frontendURL } from 'dashboard/helper/URLHelper';
import { useBranding } from 'shared/composables/useBranding';

import Button from 'dashboard/components-next/button/Button.vue';
import Label from 'dashboard/components-next/label/Label.vue';

const props = defineProps({
  id: {
    type: [String, Number],
    required: true,
  },
  name: {
    type: String,
    default: '',
  },
  description: {
    type: String,
    default: '',
  },
  enabled: {
    type: Boolean,
    default: false,
  },
  connectionStatus: {
    type: String,
    default: '',
  },
  connectionLabel: {
    type: String,
    default: '',
  },
});

const CONNECTION_STATUS = {
  not_connected: { label: 'DISABLED', color: 'slate' },
  pending: { label: 'PENDING', color: 'amber' },
  connected: { label: 'CONNECTED', color: 'teal' },
  reauthorization_required: { label: 'RECONNECTION_REQUIRED', color: 'ruby' },
};

const getters = useStoreGetters();
const accountId = getters.getCurrentAccountId;

const { t } = useI18n();
const { replaceInstallationName } = useBranding();

const connection = computed(() => CONNECTION_STATUS[props.connectionStatus]);

const integrationStatus = computed(() => {
  if (connection.value) {
    return t(`INTEGRATION_APPS.STATUS.${connection.value.label}`);
  }
  return props.enabled
    ? t('INTEGRATION_APPS.STATUS.ENABLED')
    : t('INTEGRATION_APPS.STATUS.DISABLED');
});

const integrationStatusColor = computed(() => {
  if (connection.value) return connection.value.color;
  return props.enabled ? 'teal' : 'slate';
});

const actionURL = computed(() =>
  frontendURL(`accounts/${accountId.value}/settings/integrations/${props.id}`)
);
</script>

<template>
  <div
    class="flex flex-col flex-1 p-4 m-px outline outline-n-container outline-1 bg-n-card rounded-xl"
  >
    <div class="flex items-start justify-between">
      <div class="flex h-12 w-12 mb-2">
        <img
          :src="`/dashboard/images/integrations/${id}.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm block dark:hidden bg-n-alpha-3 dark:bg-n-alpha-2"
        />
        <img
          :src="`/dashboard/images/integrations/${id}-dark.png`"
          class="max-w-full rounded-md border border-n-weak shadow-sm hidden dark:block bg-n-alpha-3 dark:bg-n-alpha-2"
        />
      </div>
      <Label
        :label="integrationStatus"
        :color="integrationStatusColor"
        compact
      />
    </div>
    <div class="flex flex-col m-0 flex-1">
      <div
        class="font-medium mb-2 text-n-slate-12 flex justify-between items-center"
      >
        <span class="text-heading-3 text-n-slate-12">{{ name }}</span>
        <router-link :to="actionURL">
          <Button
            :label="$t('INTEGRATION_APPS.CONFIGURE')"
            icon="i-woot-settings"
            link
            xs
          />
        </router-link>
      </div>
      <p class="text-n-slate-11 text-body-main">
        {{ replaceInstallationName(description) }}
      </p>
      <div
        v-if="connectionLabel"
        class="flex items-center gap-2 px-3 py-2 mt-3 rounded-lg bg-n-teal-3 outline outline-1 outline-n-teal-5"
        :title="connectionLabel"
      >
        <span class="i-lucide-circle-check size-4 shrink-0 text-n-teal-11" />
        <span class="text-sm font-medium truncate text-n-teal-11">
          {{ connectionLabel }}
        </span>
      </div>
    </div>
  </div>
</template>
